/* eslint-disable no-param-reassign */
import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import { IMAGE_PATH } from '../../utilities/constants';
import { Discipline } from '../../utilities/enums';
import { groupBy } from '../../utilities/utilityFunctions';
import { IAuditLogData } from '../AuditLogs/AuditLogs.types';
import useStyles from './ActiveDeficienciesAuditLogs.style';

interface Props {
  getValue: (arg0: IAuditLogData, arg1: string) => string | number;
  sortBasedOnDate: (arg0: IAuditLogData, arg1: IAuditLogData) => number;
  auditData: IAuditLogData[];
  getDate: (arg0: string) => string;
  showData: (arg0: IAuditLogData) => boolean;
  searchText: string;
}

const ActiveDeficienciesAuditLogs: React.FC<Props> = ({
  getValue,
  sortBasedOnDate,
  auditData,
  getDate,
  showData,
  searchText,
}) => {
  const classes = useStyles();
  const { SiteDiscipline } = useMst();
  const isElectrical = SiteDiscipline.disciplineId === Discipline.Electrical;
  const [deficiencyFieldsAuditList, setDeficiencyFields] = useState<IAuditLogData[]>([]);
  const [reasonForRemovalFieldsAuditList, setReasonForRemovalFields] = useState<IAuditLogData[]>(
    [],
  );
  const [deficiencyActionFieldsAuditList, setDeficiencyActionFieldsList] = useState<
    IAuditLogData[]
  >([]);

  const auditLogHistoryHeader = [
    'Field Name',
    'Past Value',
    'Updated Value',
    isElectrical ? 'PM Id' : 'Inspection Id',
    'Updated By',
    'Time Stamp',
  ];

  const orderOfDeficiencyFields = [
    'Deficiency File',
    'Deficiency Details',
    'Primary Cause of Failure',
    'Component Name',
    'Component Location',
    'Location Comment',
    'Date Observed',
    'Component Type',
    'Component Type Details',
    'Remedial Action',
    'Remedial Action Details',
    'Conditional Rating',
    'Urgency',
    'Operational Downtime',
    'Traffic Safety Score',
    'Reason Removal',
    'Comments',
    'Review Comment',
    'Attention Flag',
  ];

  const orderOfDeficiencyFieldsForElectrical = [
    'Deficiency File',
    'Completed as part of Preventative Maintenance',
    'Date Completed',
    'Component Name',
    'Component Location',
    'Remedial Action',
    'Primary Cause of Failure',
    'Component Type',
    'Deficiency Details',
    'Repair Factor',
    'Reason Removal',
    'Comments',
    'Review Comment',
  ];

  const riskFields = [
    'Condition Rating',
    'Operations Downtime',
    'Urgency',
    'Repair Factor',
    'Traffic Safety',
  ];

  const removeFromPlanningFields = ['ComponentRemovalCategory', 'Comments'];
  const deficiencyActionFields = [
    'Work Package',
    'Work Request',
    'Work Order',
    'CapMap Project Id',
    'CapMap Task Id',
    'Temporary Mitigation',
    'Deficiency Note',
  ];

  const groupByDeficiencyActions = () => {
    const filterDeficiencyActionsAudit = auditData.filter(item =>
      deficiencyActionFields.includes(item.field),
    );
    const orderByDeficiencyActions = groupBy(filterDeficiencyActionsAudit, 'field');
    setDeficiencyActionFieldsList(orderByDeficiencyActions);
  };

  const groupByReasonForRemoval = () => {
    const filterRemovaFromPlanningAudit = auditData.filter(item =>
      removeFromPlanningFields.includes(item.field),
    );
    const orderByRemoveFromPlanning = groupBy(filterRemovaFromPlanningAudit, 'field');
    orderByRemoveFromPlanning['Reason For Removal'] =
      orderByRemoveFromPlanning?.ComponentRemovalCategory;
    delete orderByRemoveFromPlanning?.ComponentRemovalCategory;
    setReasonForRemovalFields(orderByRemoveFromPlanning);
  };

  const groupAndOrderByDeficiencyFields = () => {
    const sortDeficiencyFields = isElectrical
      ? orderOfDeficiencyFieldsForElectrical
      : orderOfDeficiencyFields;
    const filterDeficiencyFieldsAudit = auditData.filter(
      item => !removeFromPlanningFields.includes(item.field),
    );
    const groupedByDeficiencyFields = groupBy(filterDeficiencyFieldsAudit, 'field');
    groupedByDeficiencyFields['Component Name'] = [
      ...(groupedByDeficiencyFields?.Name || []),
      ...(groupedByDeficiencyFields?.Deficiency || []),
    ];
    if (groupedByDeficiencyFields?.Risk) {
      riskFields.forEach(
        fields =>
          (groupedByDeficiencyFields[fields] = groupedByDeficiencyFields?.Risk?.filter(
            (risk: { oldValue: string | string[] }) => risk.oldValue.includes(fields),
          ).map((item: { oldValue: string; newValue: string }) => {
            item.oldValue = item.oldValue.replace(`${fields} | `, '');
            item.newValue = item.newValue.replace(`${fields} | `, '');
            return item;
          })),
      );
      groupedByDeficiencyFields['Conditional Rating'] =
        groupedByDeficiencyFields['Condition Rating'];
      groupedByDeficiencyFields['Operational Downtime'] =
        groupedByDeficiencyFields['Operations Downtime'];
      groupedByDeficiencyFields['Traffic Safety Score'] =
        groupedByDeficiencyFields['Traffic Safety'];
      delete groupedByDeficiencyFields.Risk;
    }
    groupedByDeficiencyFields['Deficiency File'] = [
      ...(groupedByDeficiencyFields?.FileDescription || []),
      ...(groupedByDeficiencyFields?.ShowOnReport || []),
      ...(groupedByDeficiencyFields?.['Deficiency File'] || []),
    ];
    groupedByDeficiencyFields['Attention Flag'] = groupedByDeficiencyFields.AttentionFlag;
    if (isElectrical) {
      groupedByDeficiencyFields['Completed as part of Preventative Maintenance'] =
        groupedByDeficiencyFields?.IsCompletedDuringInspection;
    }
    const orderedByDeficiencyFields = Object.fromEntries(
      sortDeficiencyFields.map(item => {
        return [item, groupedByDeficiencyFields[item]];
      }),
    );
    setDeficiencyFields(orderedByDeficiencyFields);
  };

  useEffect(() => {
    if (auditData?.length) {
      groupAndOrderByDeficiencyFields();
      groupByReasonForRemoval();
      groupByDeficiencyActions();
    }
  }, [auditData]);

  const showDataBasedOnSearchText = (auditDataList: IAuditLogData[], fieldName: string) => {
    const isFieldNameMatched = fieldName?.toLowerCase().includes(searchText?.toLowerCase());

    const isAuditDataForFieldMatched = auditDataList?.some(
      audit =>
        showData(audit) ||
        String(audit?.inspectionId)?.toLowerCase().includes(searchText?.toLowerCase()),
    );
    return isFieldNameMatched || isAuditDataForFieldMatched;
  };

  const renderAuditFields = (audit: IAuditLogData, index: number, isActionFields = false) => {
    return (
      <>
        {!isActionFields && index !== 0 && <Grid item xs={2}></Grid>}
        <Grid item xs={10} className={classes.fieldRow}>
          <Grid item xs={2} className={classes.activeDefoldValueGrid}>
            {getValue(audit, 'oldValue')}
            <img src={`${IMAGE_PATH}/arrowRight.svg`} alt="View" className={classes.arrowRight} />
          </Grid>
          <Grid item xs={2}>
            {getValue(audit, 'newValue')}
          </Grid>
          <Grid item xs={2} className={classes.activeDefTableCellAlignRight}>
            {audit?.inspectionId || '-'}
          </Grid>
          <Grid item xs={2} className={classes.activeDefUserNameField}>
            {audit?.userName || ''}
          </Grid>
          <Grid item xs={2} className={classes.activeDefTableCellAlignRight}>
            {getDate(audit?.actionDate) || ''}
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Grid className={classes.card1}>
      <Grid container xs={12} className={classes.activeDefHeaderGrid}>
        {auditLogHistoryHeader.map((title, titleIndex) => {
          return (
            <Grid
              item
              xs={2}
              key={titleIndex}
              className={
                titleIndex > 2
                  ? isElectrical
                    ? classes.electricalActiveDef
                    : classes.activeDefHeaderAlignRight
                  : null
              }
            >
              {title}
            </Grid>
          );
        })}
      </Grid>
      {[
        deficiencyFieldsAuditList,
        reasonForRemovalFieldsAuditList,
        deficiencyActionFieldsAuditList,
      ].map((fields, index) => {
        return (
          <Grid
            container
            xs={12}
            className={index !== 0 ? classes.activeDeficiencyRemovalGrid : ''}
            key={index}
          >
            {Object.keys(fields)
              .filter(
                auditField =>
                  fields[auditField]?.length &&
                  showDataBasedOnSearchText(fields[auditField], auditField),
              )
              .map((auditName, auditIndex: number) => {
                return (
                  <Grid
                    container
                    xs={12}
                    className={
                      index !== 0 ? classes.activeDefRemovalGrid : classes.activeDefContentGrid
                    }
                    key={auditIndex}
                  >
                    <Grid item xs={2} className={classes.activeDeficienctField}>
                      {auditName}
                    </Grid>
                    {fields[auditName]
                      ?.sort((a: IAuditLogData, b: IAuditLogData) => {
                        return sortBasedOnDate(a, b);
                      })
                      ?.map((auditObj: IAuditLogData, auditObjIndex: number) => {
                        return renderAuditFields(auditObj, auditObjIndex);
                      })}
                  </Grid>
                );
              })}
          </Grid>
        );
      })}
    </Grid>
  );
};

export default observer(ActiveDeficienciesAuditLogs);
